import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FaBuilding,
  FaChevronUp,
  FaChevronDown,
  FaWarehouse,
  FaBalanceScale,
  FaPercentage,
  FaCashRegister,
  FaTruckLoading,
  FaFileInvoiceDollar,
  FaUnlock,
  FaCheckCircle,
  FaCheckDouble,
  FaTimesCircle,
  FaTrashAlt,
  FaPrint,
  FaTags,
  FaEdit,
  FaLevelDownAlt,
  FaBoxes,
  FaRandom,
  FaBookOpen,
  FaTools,
  FaCalendarAlt,
  FaFileSignature,
  FaExchangeAlt,
} from "react-icons/fa";
import {
  BsPersonFill,
  BsBoxSeam,
  BsCollectionFill,
  BsFillFileEarmarkRuledFill,
  BsFillClipboard2CheckFill,
} from "react-icons/bs";
import {
  MdOutlineAccountTree,
  MdOutlineAttachMoney,
  MdCategory,
  MdCalculate,
  MdSettings,
} from "react-icons/md";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import GridViewIcon from "@mui/icons-material/GridView";
import {
  FiShoppingCart,
  FiClipboard,
  FiDatabase,
  FiSearch,
  FiBarChart2,
} from "react-icons/fi";
import Logo from "../All images/Logo.png";
import EEC_Logo from "../All images/EEC-Logo.png";
import Logo_MBCS from "../All images/Logo_MBC.jpg";
import { useAuth } from "../Components/StoredDetails";
import { useWindowContext } from "../Context/WindowContext";
import { renderFormComponent } from "./formRegistry";
import "../Layout/Layout.css";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const logoutButtonStates = {
  default: {
    "--figure-duration": "100ms",
    "--transform-figure": "none",
    "--walking-duration": "100ms",
    "--transform-arm1": "none",
    "--transform-wrist1": "none",
    "--transform-arm2": "none",
    "--transform-wrist2": "none",
    "--transform-leg1": "none",
    "--transform-calf1": "none",
    "--transform-leg2": "none",
    "--transform-calf2": "none",
  },
  hover: {
    "--figure-duration": "100ms",
    "--transform-figure": "translateX(1.5px)",
    "--walking-duration": "100ms",
    "--transform-arm1": "rotate(-5deg)",
    "--transform-wrist1": "rotate(-15deg)",
    "--transform-arm2": "rotate(5deg)",
    "--transform-wrist2": "rotate(6deg)",
    "--transform-leg1": "rotate(-10deg)",
    "--transform-calf1": "rotate(5deg)",
    "--transform-leg2": "rotate(20deg)",
    "--transform-calf2": "rotate(-20deg)",
  },
  walking1: {
    "--figure-duration": "300ms",
    "--transform-figure": "translateX(11px)",
    "--walking-duration": "300ms",
    "--transform-arm1": "translateX(-4px) translateY(-2px) rotate(120deg)",
    "--transform-wrist1": "rotate(-5deg)",
    "--transform-arm2": "translateX(4px) rotate(-110deg)",
    "--transform-wrist2": "rotate(-5deg)",
    "--transform-leg1": "translateX(-3px) rotate(80deg)",
    "--transform-calf1": "rotate(-30deg)",
    "--transform-leg2": "translateX(4px) rotate(-60deg)",
    "--transform-calf2": "rotate(20deg)",
  },
  walking2: {
    "--figure-duration": "400ms",
    "--transform-figure": "translateX(17px)",
    "--walking-duration": "300ms",
    "--transform-arm1": "rotate(60deg)",
    "--transform-wrist1": "rotate(-15deg)",
    "--transform-arm2": "rotate(-45deg)",
    "--transform-wrist2": "rotate(6deg)",
    "--transform-leg1": "rotate(-5deg)",
    "--transform-calf1": "rotate(10deg)",
    "--transform-leg2": "rotate(10deg)",
    "--transform-calf2": "rotate(-20deg)",
  },
  falling1: {
    "--figure-duration": "1600ms",
    "--walking-duration": "400ms",
    "--transform-arm1": "rotate(-60deg)",
    "--transform-wrist1": "none",
    "--transform-arm2": "rotate(30deg)",
    "--transform-wrist2": "rotate(120deg)",
    "--transform-leg1": "rotate(-30deg)",
    "--transform-calf1": "rotate(-20deg)",
    "--transform-leg2": "rotate(20deg)",
  },
  falling2: {
    "--walking-duration": "300ms",
    "--transform-arm1": "rotate(-100deg)",
    "--transform-arm2": "rotate(-60deg)",
    "--transform-wrist2": "rotate(60deg)",
    "--transform-leg1": "rotate(80deg)",
    "--transform-calf1": "rotate(20deg)",
    "--transform-leg2": "rotate(-60deg)",
  },
  falling3: {
    "--walking-duration": "500ms",
    "--transform-arm1": "rotate(-30deg)",
    "--transform-wrist1": "rotate(40deg)",
    "--transform-arm2": "rotate(50deg)",
    "--transform-wrist2": "none",
    "--transform-leg1": "rotate(-30deg)",
    "--transform-leg2": "rotate(20deg)",
    "--transform-calf2": "none",
  },
};

const AnimatedLogoutButton = ({ onLogout }) => {
  const [state, setState] = useState("default");
  const timersRef = useRef([]);

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
  }, []);

  const schedule = (callback, delay) => {
    const timer = setTimeout(callback, delay);
    timersRef.current.push(timer);
  };

  const handleClick = () => {
    if (state !== "default" && state !== "hover") return;

    setState("walking1");
    schedule(() => {
      setState("walking2");
      schedule(() => {
        setState("falling1");
        schedule(onLogout, 100);
      }, 150);
    }, 150);
  };

  const className = [
    "logoutButton",
    state !== "default" && state !== "hover" ? "clicked" : "",
    state === "walking2" || state.startsWith("falling") ? "door-slammed" : "",
    state.startsWith("falling") ? "falling" : "",
  ].filter(Boolean).join(" ");

  return (
    <button
      type="button"
      className={className}
      style={logoutButtonStates[state]}
      onMouseEnter={() => state === "default" && setState("hover")}
      onMouseLeave={() => state === "hover" && setState("default")}
      onClick={handleClick}
      aria-label="Log out"
    >
      <svg className="doorway" viewBox="0 0 100 100" aria-hidden="true">
        <path d="M93.4 86.3H58.6c-1.9 0-3.4-1.5-3.4-3.4V17.1c0-1.9 1.5-3.4 3.4-3.4h34.8c1.9 0 3.4 1.5 3.4 3.4v65.8c0 1.9-1.5 3.4-3.4 3.4z" />
        <path className="bang" d="M40.5 43.7L26.6 31.4l-2.5 6.7zM41.9 50.4l-19.5-4-1.4 6.3zM40 57.4l-17.7 3.9 3.9 5.7z" />
      </svg>
      <svg className="figure" viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="52.1" cy="32.4" r="6.4" />
        <path d="M50.7 62.8c-1.2 2.5-3.6 5-7.2 4-3.2-.9-4.9-3.5-4-7.8.7-3.4 3.1-13.8 4.1-15.8 1.7-3.4 1.6-4.6 7-3.7 4.3.7 4.6 2.5 4.3 5.4-.4 3.7-2.8 15.1-4.2 17.9z" />
        <g className="arm1"><path d="M55.5 56.5l-6-9.5c-1-1.5-.6-3.5.9-4.4 1.5-1 3.7-1.1 4.6.4l6.1 10c1 1.5.3 3.5-1.1 4.4-1.5.9-3.5.5-4.5-.9z" /><path className="wrist1" d="M69.4 59.9L58.1 58c-1.7-.3-2.9-1.9-2.6-3.7.3-1.7 1.9-2.9 3.7-2.6l11.4 1.9c1.7.3 2.9 1.9 2.6 3.7-.4 1.7-2 2.9-3.8 2.6z" /></g>
        <g className="arm2"><path d="M34.2 43.6L45 40.3c1.7-.6 3.5.3 4 2 .6 1.7-.3 4-2 4.5l-10.8 2.8c-1.7.6-3.5-.3-4-2-.6-1.6.3-3.4 2-4z" /><path className="wrist2" d="M27.1 56.2L32 45.7c.7-1.6 2.6-2.3 4.2-1.6 1.6.7 2.3 2.6 1.6 4.2L33 58.8c-.7 1.6-2.6 2.3-4.2 1.6-1.7-.7-2.4-2.6-1.7-4.2z" /></g>
        <g className="leg1"><path d="M52.1 73.2s-7-5.7-7.9-6.5c-.9-.9-1.2-3.5-.1-4.9 1.1-1.4 3.8-1.9 5.2-.9l7.9 7c1.4 1.1 1.7 3.5.7 4.9-1.1 1.4-4.4 1.5-5.8.4z" /><path className="calf1" d="M52.6 84.4l-1-12.8c-.1-1.9 1.5-3.6 3.5-3.7 2-.1 3.7 1.4 3.8 3.4l1 12.8c.1 1.9-1.5 3.6-3.5 3.7-2 0-3.7-1.5-3.8-3.4z" /></g>
        <g className="leg2"><path d="M37.8 72.7s1.3-10.2 1.6-11.4 2.4-2.8 4.1-2.6c1.7.2 3.6 2.3 3.4 4l-1.8 11.1c-.2 1.7-1.7 3.3-3.4 3.1-1.8-.2-4.1-2.4-3.9-4.2z" /><path className="calf2" d="M29.5 82.3l9.6-10.9c1.3-1.4 3.6-1.5 5.1-.1 1.5 1.4.4 4.9-.9 6.3l-8.5 9.6c-1.3 1.4-3.6 1.5-5.1.1-1.4-1.3-1.5-3.5-.2-5z" /></g>
      </svg>
      <svg className="door" viewBox="0 0 100 100" aria-hidden="true"><path d="M93.4 86.3H58.6c-1.9 0-3.4-1.5-3.4-3.4V17.1c0-1.9 1.5-3.4 3.4-3.4h34.8c1.9 0 3.4 1.5 3.4 3.4v65.8c0 1.9-1.5 3.4-3.4 3.4z" /><circle cx="66" cy="50" r="3.7" /></svg>
      <span className="button-text">Log Out</span>
    </button>
  );
};

function Sidebar({ sidebarOpen, onHeaderClick }) {
  const { authData, setUserData, setLocations, setSelectedLocation } =
    useAuth();
  const user = authData.user;
  const selectedLocation = authData.selectedLocation;
  const [menus, setMenus] = useState([]);
  const [openGroup, setOpenGroup] = useState(null);

  const { openWindow, activeWindowId, clearWindows } = useWindowContext();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("rememberedCredentials");
    sessionStorage.clear();
    clearWindows();
    setUserData(null);
    setLocations([]);
    setSelectedLocation(null);
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (!user?.UserID) return;

    const loadMenus = async () => {
      try {
        const { data } = await axios.post(`${API_BASE_URL}/api/menus`, {
          email: user.UserID,
          userType: user.UserType,
        });

        if (!Array.isArray(data)) {
          setMenus([]);
          return;
        }

        // Parent Menu
        const menuMap = {};

        data.forEach((row) => {
          if (Number(row.ParentCode) === 0) {
            menuMap[row.MenuCode] = {
              ...row,
              submenus: [],
            };
          }
        });

        // Child Menu
        data.forEach((row) => {
          if (Number(row.ParentCode) !== 0 && menuMap[row.ParentCode]) {
            menuMap[row.ParentCode].submenus.push(row);
          }
        });

        setMenus(Object.values(menuMap));
      } catch (err) {
        console.error("Error loading menus", err);
      }
    };

    loadMenus();
  }, [user]);

  const getMenuIcon = (menuName = "") => {
    const icons = {
      "Stores Purchase": {
        icon: <FiShoppingCart size={16} />,
        color: "#1565C0",
      },
      "Master Data": { icon: <FiDatabase size={16} />, color: "#2E7D32" },
      "Form Setting": {
        icon: <MdSettings style={{ fontSize: 16 }} />,
        color: "#FF5722",
      },
    };
    return (
      icons[menuName.trim()] || {
        icon: <FiClipboard size={16} />,
        color: "#607D8B",
      }
    );
  };

  const getSubmenuIcon = (menuName = "") => {
  const icons = {
    "Purchase Indent": {
      icon: <FiClipboard size={14} />,
      color: "#1565C0",
    },
    "Indent Wizard": {
      icon: <MdCalculate size={14} />,
      color: "#5E35B1",
    },
    "Indent Short Closing": {
      icon: <FaChevronDown size={14} />,
      color: "#6D4C41",
    },
    "Purchase Enquiry": {
      icon: <FiSearch size={14} />,
      color: "#00897B",
    },
    "Purchase Quotation": {
      icon: <BsFillFileEarmarkRuledFill size={14} />,
      color: "#7B1FA2",
    },
    "Purchase Order": {
      icon: <BsFillClipboard2CheckFill size={14} />,
      color: "#1565C0",
    },
    "PO Custom Clearance": {
      icon: <FaWarehouse size={14} />,
      color: "#795548",
    },
    "P.O. Short Closing": {
      icon: <FaChevronDown size={14} />,
      color: "#D84315",
    },
    "PO Service": {
      icon: <FaTools size={14} />,
      color: "#3949AB",
    },
    "PO Validity Extend": {
      icon: <FaCalendarAlt size={14} />,
      color: "#00838F",
    },
    "PO Proposal": {
      icon: <FaFileSignature size={14} />,
      color: "#6A1B9A",
    },
    "Change Validity (Rate Contract)": {
      icon: <FaExchangeAlt size={14} />,
      color: "#EF6C00",
    },

    "GRN Cash": {
      icon: <FaCashRegister size={14} />,
      color: "#00897B",
    },
    "GRN Against P.O.": {
      icon: <FaTruckLoading size={14} />,
      color: "#1565C0",
    },
    "GRN Against Challan": {
      icon: <FaTruckLoading size={14} />,
      color: "#2E7D32",
    },
    "Landed Cost": {
      icon: <MdOutlineAttachMoney size={14} />,
      color: "#F9A825",
    },
    "Stores CN Receving": {
      icon: <BsFillFileEarmarkRuledFill size={14} />,
      color: "#C62828",
    },
    "A P Invoice": {
      icon: <FaFileInvoiceDollar size={14} />,
      color: "#1565C0",
    },
    "AP Invoice Service": {
      icon: <FaFileInvoiceDollar size={14} />,
      color: "#00897B",
    },
    "A P Credit Note": {
      icon: <FaFileInvoiceDollar size={14} />,
      color: "#C62828",
    },
    "AP Debit Note": {
      icon: <FaFileInvoiceDollar size={14} />,
      color: "#AD1457",
    },
    "GST Input Reconsilation": {
      icon: <FaPercentage size={14} />,
      color: "#2E7D32",
    },
    "Bill Of Entry": {
      icon: <BsFillFileEarmarkRuledFill size={14} />,
      color: "#5E35B1",
    },
    "Document Un Approval": {
      icon: <FaUnlock size={14} />,
      color: "#EF6C00",
    },
    "Approve Document": {
      icon: <FaCheckCircle size={14} />,
      color: "#2E7D32",
    },
    "Document Approval": {
      icon: <FaCheckDouble size={14} />,
      color: "#1565C0",
    },
    "Stores Cancelllation": {
      icon: <FaTimesCircle size={14} />,
      color: "#C62828",
    },
    "Stores Delete": {
      icon: <FaTrashAlt size={14} />,
      color: "#D32F2F",
    },
    "Stores Document Print": {
      icon: <FaPrint size={14} />,
      color: "#455A64",
    },

    "Stores Dash Board": {
      icon: <GridViewIcon style={{ fontSize: 14 }} />,
      color: "#7C4DFF",
    },
    "Stock Position": {
      icon: <FiBarChart2 size={14} />,
      color: "#00897B",
    },
    "Sticker Print": {
      icon: <FaTags size={14} />,
      color: "#8E24AA",
    },
    "Item Master Data": {
      icon: <BsBoxSeam size={14} />,
      color: "#00897B",
    },
    "Item Name Correction": {
      icon: <FaEdit size={14} />,
      color: "#F57C00",
    },
    "Min Stock Level SetUP": {
      icon: <FaLevelDownAlt size={14} />,
      color: "#C62828",
    },
    "Department Master": {
      icon: <MdOutlineAccountTree size={14} />,
      color: "#3949AB",
    },
    "Unit Master": {
      icon: <FaBalanceScale size={14} />,
      color: "#F9A825",
    },
    "Initial Quantity": {
      icon: <FaBoxes size={14} />,
      color: "#2E7D32",
    },
    "Re-Arrange Inventory": {
      icon: <FaRandom size={14} />,
      color: "#5E35B1",
    },
    "Stock Journal": {
      icon: <FaBookOpen size={14} />,
      color: "#1565C0",
    },
    "Stores Document UnApproval": {
      icon: <FaUnlock size={14} />,
      color: "#EF6C00",
    },

    "Item Master": {
      icon: <BsBoxSeam size={14} />,
      color: "#00897B",
    },
    "Warehouse": {
      icon: <FaWarehouse size={14} />,
      color: "#8D6E63",
    },
    "Item Group": {
      icon: <MdCategory size={14} />,
      color: "#7B1FA2",
    },
    "Unit": {
      icon: <FaBalanceScale size={14} />,
      color: "#F9A825",
    },
    "ODTP": {
      icon: <BsFillFileEarmarkRuledFill size={14} />,
      color: "#455A64",
    },
    "Department": {
      icon: <MdOutlineAccountTree size={14} />,
      color: "#3949AB",
    },
    "Budget": {
      icon: <MdOutlineAttachMoney size={14} />,
      color: "#2E7D32",
    },
    "TaxCode": {
      icon: <FaPercentage size={14} />,
      color: "#C62828",
    },
    "FRMS Settings": {
      icon: <NoteAddIcon style={{ fontSize: 14 }} />,
      color: "#00AEEF",
    },
    "GRDS Settings": {
      icon: <GridViewIcon style={{ fontSize: 14 }} />,
      color: "#7C4DFF",
    },
    "Calculation Settings": {
      icon: <MdCalculate size={14} />,
      color: "#E4558A",
    },
  };

  return (
    icons[menuName.trim()] || {
      icon: <BsCollectionFill size={14} />,
      color: "#1135e9",
    }
  );
};

  const handleOpen = (page) => {
    const content =
      typeof page.content === "function" ? page.content() : page.content;

    openWindow({
      id: page.id,
      title: page.title,
      icon: page.icon,
      content,
      pageKey: page.pageKey || page.title,
    });
  };

  const getPageFormName = (page = {}) => {
    return page?.DesktopFrmName || page?.FrmName || page?.MenuName || "";
  };

  const loadComponent = (formName) => {
    return renderFormComponent(formName, formName || "Unknown");
  };

  const currentCompCode = selectedLocation?.CompCode || "";

  const companyLogos = {
    EECPL: EEC_Logo,
    SIPL: Logo,
    MBCS: Logo_MBCS,
  };

  const companyLogo = companyLogos[currentCompCode] || Logo;

  return (
    <aside className={`Sidebar ${sidebarOpen ? "" : "Sidebar-Close"}`}>
      <div
        className="Sidebar-Header"
        onClick={onHeaderClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onHeaderClick?.();
          }
        }}
      >
        <div className="Sidebar-brand-logo">
          <img
            src={companyLogo}
            alt={`${currentCompCode} Logo`}
            className="company-img"
          />
        </div>
        <div>
          <div className="company-info" style={{ cursor: "pointer" }}>
            <div className="company-name">
              <FaBuilding className="company-icon" />
              {user?.CompName || selectedLocation?.CompName || ""}
            </div>
            <div className="User-info">
              <div className="User-Name">
                <BsPersonFill className="User-Icon" />
                {user?.UserName || user?.EName || ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sidebar-menu-scroll">
        <div className="sidebar-menu">
          {menus.map((group) => {
            const menuIcon = getMenuIcon(group.MenuName);
            const hasChildren = group.submenus?.length > 0;

            return (
              <div key={group.MenuCode} className="menu-item">
                {/* Main Menu */}

                <div
                  className={`menu-main-item ${
                    openGroup === group.MenuCode ? "active" : ""
                  }`}
                  onClick={() => {
                    if (hasChildren) {
                      setOpenGroup(
                        openGroup === group.MenuCode ? null : group.MenuCode,
                      );
                    } else {
                      handleOpen({
                        id: `${group.MenuCode}-${Date.now()}`,
                        title: group.MenuName,
                        icon: menuIcon?.icon,
                        content: loadComponent(getPageFormName(group)),
                        pageKey: getPageFormName(group) || group.MenuName,
                      });
                    }
                  }}
                >
                  {menuIcon && (
                    <span
                      className="menu-icon"
                      style={{ color: menuIcon.color }}
                    >
                      {menuIcon.icon}
                    </span>
                  )}

                  <span className="menu-text">{group.MenuName}</span>

                  {hasChildren && (
                    <span className="menu-arrow">
                      {openGroup === group.MenuCode ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}
                    </span>
                  )}
                </div>

                {/* Sub Menu */}

                {hasChildren && openGroup === group.MenuCode && (
                  <div className="submenu">
                    {group.submenus.map((submenu) => {
                      const subIcon = getSubmenuIcon(submenu.MenuName);

                      return (
                        <div
                          key={submenu.MenuCode}
                          className={`submenu-item ${
                            activeWindowId?.startsWith(`${submenu.MenuCode}-`)
                              ? "active-submenu"
                              : ""
                          }`}
                          onClick={() =>
                            handleOpen({
                              id: `${submenu.MenuCode}-${Date.now()}`,
                              title: submenu.MenuName,
                              icon: subIcon?.icon,
                              content: loadComponent(getPageFormName(submenu)),
                              pageKey:
                                getPageFormName(submenu) || submenu.MenuName,
                            })
                          }
                        >
                          {subIcon && (
                            <span
                              className="submenu-icon"
                              style={{
                                color: subIcon.color,
                              }}
                            >
                              {subIcon.icon}
                            </span>
                          )}

                          <span className="submenu-text">
                            {submenu.MenuName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="Sidebar-Logo-container">
        <p className="Sidebar-Logo-Container-text">
          <span className="logo-sfactor" style={{ fontSize: "1.25rem" }}>
            SFactor
          </span>
          <span className="Sidebar-Logo-Details">
            <span className="logo-erp">ERP </span>
            <span className="logo-version">
              <a
                href="https://sfactorerp.com/version/3.0"
                target="_blank"
                rel="noopener noreferrer"
              >
                1.0.2.0
              </a>
            </span>
          </span>
        </p>
        <div className="Logout-Container">
          {/* <button
            type="button"
            className="Sidebar-Logout-Button"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
          >
            <FiLogOut aria-hidden="true" />
          </button> */}

          <AnimatedLogoutButton onLogout={handleLogout} />
          {false && <div className="background background--light">
  <button class="logoutButton logoutButton--dark">
    <svg class="doorway" viewBox="0 0 100 100">
      <path d="M93.4 86.3H58.6c-1.9 0-3.4-1.5-3.4-3.4V17.1c0-1.9 1.5-3.4 3.4-3.4h34.8c1.9 0 3.4 1.5 3.4 3.4v65.8c0 1.9-1.5 3.4-3.4 3.4z" />
      <path class="bang" d="M40.5 43.7L26.6 31.4l-2.5 6.7zM41.9 50.4l-19.5-4-1.4 6.3zM40 57.4l-17.7 3.9 3.9 5.7z" />
    </svg>
    <svg class="figure" viewBox="0 0 100 100">
      <circle cx="52.1" cy="32.4" r="6.4" />
      <path d="M50.7 62.8c-1.2 2.5-3.6 5-7.2 4-3.2-.9-4.9-3.5-4-7.8.7-3.4 3.1-13.8 4.1-15.8 1.7-3.4 1.6-4.6 7-3.7 4.3.7 4.6 2.5 4.3 5.4-.4 3.7-2.8 15.1-4.2 17.9z" />
      <g class="arm1">
        <path d="M55.5 56.5l-6-9.5c-1-1.5-.6-3.5.9-4.4 1.5-1 3.7-1.1 4.6.4l6.1 10c1 1.5.3 3.5-1.1 4.4-1.5.9-3.5.5-4.5-.9z" />
        <path class="wrist1" d="M69.4 59.9L58.1 58c-1.7-.3-2.9-1.9-2.6-3.7.3-1.7 1.9-2.9 3.7-2.6l11.4 1.9c1.7.3 2.9 1.9 2.6 3.7-.4 1.7-2 2.9-3.8 2.6z" />
      </g>
      <g class="arm2">
        <path d="M34.2 43.6L45 40.3c1.7-.6 3.5.3 4 2 .6 1.7-.3 4-2 4.5l-10.8 2.8c-1.7.6-3.5-.3-4-2-.6-1.6.3-3.4 2-4z" />
        <path class="wrist2" d="M27.1 56.2L32 45.7c.7-1.6 2.6-2.3 4.2-1.6 1.6.7 2.3 2.6 1.6 4.2L33 58.8c-.7 1.6-2.6 2.3-4.2 1.6-1.7-.7-2.4-2.6-1.7-4.2z" />
      </g>
      <g class="leg1">
        <path d="M52.1 73.2s-7-5.7-7.9-6.5c-.9-.9-1.2-3.5-.1-4.9 1.1-1.4 3.8-1.9 5.2-.9l7.9 7c1.4 1.1 1.7 3.5.7 4.9-1.1 1.4-4.4 1.5-5.8.4z" />
        <path class="calf1" d="M52.6 84.4l-1-12.8c-.1-1.9 1.5-3.6 3.5-3.7 2-.1 3.7 1.4 3.8 3.4l1 12.8c.1 1.9-1.5 3.6-3.5 3.7-2 0-3.7-1.5-3.8-3.4z" />
      </g>
      <g class="leg2">
        <path d="M37.8 72.7s1.3-10.2 1.6-11.4 2.4-2.8 4.1-2.6c1.7.2 3.6 2.3 3.4 4l-1.8 11.1c-.2 1.7-1.7 3.3-3.4 3.1-1.8-.2-4.1-2.4-3.9-4.2z" />
        <path class="calf2" d="M29.5 82.3l9.6-10.9c1.3-1.4 3.6-1.5 5.1-.1 1.5 1.4.4 4.9-.9 6.3l-8.5 9.6c-1.3 1.4-3.6 1.5-5.1.1-1.4-1.3-1.5-3.5-.2-5z" />
      </g>
    </svg>
    <svg class="door" viewBox="0 0 100 100">
      <path d="M93.4 86.3H58.6c-1.9 0-3.4-1.5-3.4-3.4V17.1c0-1.9 1.5-3.4 3.4-3.4h34.8c1.9 0 3.4 1.5 3.4 3.4v65.8c0 1.9-1.5 3.4-3.4 3.4z" />
      <circle cx="66" cy="50" r="3.7" />
    </svg>
    <span class="button-text">Log Out</span>
  </button>
</div>}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
